'use client';

import { Suspense, use } from 'react';
import { Configure, Index } from 'react-instantsearch';
import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material/styles';
import { AnalyticsEventNames } from '@/analytics/Event.types';
import { AnalyticsPageNames } from '@/analytics/Page.types';
import { analytics } from '@/analytics/segment';
import Link from '@/components/Link';
import { BuyerListingFavorite } from '@/legacy/favorites/FavoritesViewModel';
import { useGhostStore } from '@/legacy/store';
import { SectionColorContext } from './Collections/SectionColorContext';
import { ALL_SECTION_INDEX } from './Collections/types';
import { AllInventoryHits } from './AllInventoryHits';
import { ListingCardsSkeleton } from './ListingCardsSkeleton';
import Section from './Section';
import { Background } from './types';

export default function AllInventory({
  favoritesPromise,
  listingsIndexName,
  background: defaultBackground,
  isStockXSectionEnabled,
}: {
  favoritesPromise: Promise<BuyerListingFavorite[]>;
  listingsIndexName: string;
  background?: Background;
  isStockXSectionEnabled: boolean;
}): JSX.Element {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.only('mobile'));
  const hitsPerPage = isMobile ? 8 : 16;

  const { backgrounds } = use(SectionColorContext);

  const background = backgrounds ? backgrounds.get(ALL_SECTION_INDEX) : defaultBackground;

  return (
    <Section background={background}>
      <Box>
        <Stack flexDirection="column" mb={2}>
          <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
            <Typography marginBottom={1} variant={isMobile ? 'h3' : 'h2'}>
              All Listings
            </Typography>
            <Link
              variant="link"
              color="typographyDarkPrimary"
              href="/collection/all"
              whiteSpace="nowrap"
              data-testid="view-all-link-all"
              ml={3}
              onClick={() =>
                // @NOTE Not typed, but same shape as ListingsCollectionSection Analytics!!
                analytics.track(AnalyticsEventNames.HOME_SECTION_VIEW_ALL_CLICKED, {
                  context: { page: AnalyticsPageNames.BUYER_HOME, section: 'All Inventory' },
                })
              }
            >
              View All
            </Link>
          </Stack>
        </Stack>

        <Box width="100%">
          <Grid container columnSpacing={3} rowSpacing={5}>
            <Suspense fallback={<ListingCardsSkeleton hitsPerPage={hitsPerPage} />}>
              <AllInventoryContent
                favoritesPromise={favoritesPromise}
                listingsIndexName={listingsIndexName}
                hitsPerPage={hitsPerPage}
                isStockXSectionEnabled={isStockXSectionEnabled}
              />
            </Suspense>
          </Grid>
          <Stack
            paddingTop={5}
            direction="row"
            width="100%"
            alignItems={'center'}
            justifyContent="center"
          >
            <Button
              data-testid="button-viewAllInventory"
              LinkComponent={Link}
              onClick={() => {
                analytics.track(AnalyticsEventNames.HOME_ALL_INVENTORY_VIEW_MORE_CLICKED);
              }}
              href="/collection/all"
              variant="outlined"
            >
              View All
            </Button>
          </Stack>
        </Box>
      </Box>
    </Section>
  );
}

function AllInventoryContent({
  favoritesPromise,
  listingsIndexName,
  hitsPerPage,
  isStockXSectionEnabled,
}: {
  favoritesPromise: Promise<BuyerListingFavorite[]>;
  listingsIndexName: string;
  hitsPerPage: number;
  isStockXSectionEnabled: boolean;
}): JSX.Element {
  const buyerId = useGhostStore((state) => state.user)?.buyer?.id;
  const stealthListingFilter = buyerId
    ? `(stealthApprovedBuyerIds:${buyerId} OR isStealth:false)`
    : 'isStealth:false';

  return (
    <Index indexName={listingsIndexName} indexId="all-inventory">
      <Configure hitsPerPage={hitsPerPage} filters={stealthListingFilter} />
      <AllInventoryHits
        favoritesPromise={favoritesPromise}
        isStockXSectionEnabled={isStockXSectionEnabled}
      />
    </Index>
  );
}
